import React, { useState, useEffect } from 'react';
import { CHANGELOG, LATEST_VERSION } from '../data/changelog';
import TermsModal from './TermsModal';   // For the "View Terms" link in the footer

// ─── Inline bell icon ──────────────────────────────────────────
// Matches the style of TopBar.jsx icons — uses currentColor so it
// inherits the button's text color (#6a9fc0 by default).
const IconBell = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {/* Bell body — rounded shoulders, flared base */}
    <path d="M12 3 C8.5 3 6 5.5 6 9 V13 L4 16 H20 L18 13 V9 C18 5.5 15.5 3 12 3 Z"
      stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    {/* Clapper at the bottom */}
    <path d="M10 19 C10 20 11 21 12 21 C13 21 14 20 14 19"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// ─── Tag colors per change type ────────────────────────────────
// Matches the navy theme palette used elsewhere in the app
const TAG_STYLES = {
  new: {
    color: '#00cc88',
    bg: 'rgba(0,204,136,0.1)',
    border: 'rgba(0,204,136,0.35)',
    label: 'NEW',
  },
  improvement: {
    color: '#3399ff',
    bg: 'rgba(51,153,255,0.1)',
    border: 'rgba(51,153,255,0.35)',
    label: 'IMPROVED',
  },
  fix: {
    color: '#ff9900',
    bg: 'rgba(255,153,0,0.1)',
    border: 'rgba(255,153,0,0.35)',
    label: 'FIXED',
  },
};

// localStorage key for tracking which version the user has seen
const SEEN_KEY = 'sb_whats_new_seen_version';

// Read-only check used by TopBar to put the red dot on the ⋮ button itself.
// Reads localStorage directly (cheap) so the dot clears on TopBar's next
// render after the user opens What's New — e.g. when the kebab closes.
export function hasUnreadWhatsNew() {
  try {
    return (localStorage.getItem(SEEN_KEY) || '0') !== LATEST_VERSION;
  } catch {
    return false; // storage blocked — don't nag with a dot we can never clear
  }
}

export default function WhatsNew({ renderAs = 'button' }) {
  const [open, setOpen] = useState(false);
  // Controls whether the Terms review modal is shown over the WhatsNew modal
  const [showTerms, setShowTerms] = useState(false);
  // Tracks the version the user has already acknowledged (read from localStorage)
  const [seenVersion, setSeenVersion] = useState(() => {
    try {
      return localStorage.getItem(SEEN_KEY) || '0';
    } catch {
      return '0';
    }
  });

  // True if the user hasn't seen the latest version yet — drives the red badge
  const hasUnread = seenVersion !== LATEST_VERSION;

  // When the modal opens, mark the latest version as seen
  const handleOpen = () => {
    setOpen(true);
    try {
      localStorage.setItem(SEEN_KEY, LATEST_VERSION);
      setSeenVersion(LATEST_VERSION);
    } catch {
      // localStorage might fail in private mode — just update state
      setSeenVersion(LATEST_VERSION);
    }
  };

  // Close on Escape key for keyboard friendliness
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      {/* ─── Trigger — two render modes ─────────────────────────────
          'button'  (default): the original standalone topbar button with
                                a bordered pill + absolute red badge.
          'menuRow': flush menu row matching the kebab dropdown style —
                     no border, no rounded pill, badge sits inline. */}
      {renderAs === 'menuRow' ? (
        <button
          onClick={handleOpen}
          style={{
            // Flush row: no border, no pill — just a hoverable strip
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', background: 'transparent',
            border: 'none', borderRadius: 6,
            color: '#c8dff0',
            fontSize: 13, fontWeight: 500,
            padding: '9px 12px',
            cursor: 'pointer', fontFamily: 'inherit',
            textAlign: 'left',
            transition: 'background .12s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(51,153,255,.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          {/* Bell — muted blue to match other kebab row icons */}
          <span style={{ color: '#6a9fc0', display: 'flex' }}>
            <IconBell size={14} />
          </span>
          <span style={{ flex: 1 }}>What's New</span>
          {/* Red unread dot — sits inline at the right edge of the row */}
          {hasUnread && (
            <span
              style={{
                width: 8, height: 8, borderRadius: '50%',
                background: '#ff4444',
                boxShadow: '0 0 6px rgba(255,68,68,0.6)',
                flexShrink: 0,
              }}
            />
          )}
        </button>
      ) : (
        // ─── Original standalone bell button (unchanged) ───
        <button
          onClick={handleOpen}
          title="What's New"
          style={{
            background: 'transparent',
            border: '1px solid #1e3a54',
            borderRadius: 7,
            color: '#6a9fc0',
            fontSize: 11,
            fontWeight: 500,
            padding: '6px 14px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            position: 'relative',                       // anchor for the badge
          }}
        >
          {/* Was 🔔 emoji — IconBell inherits the button color */}
          <IconBell size={14} />
          What's New
          {/* Red dot badge — shown only if user hasn't opened the modal at the latest version */}
          {hasUnread && (
            <span
              style={{
                position: 'absolute',
                top: -3,
                right: -3,
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: '#ff4444',
                border: '1.5px solid #060d18',          // matches topbar bg so it punches out
                boxShadow: '0 0 6px rgba(255,68,68,0.6)',
              }}
            />
          )}
        </button>
      )}

      {/* ─── Modal ─── */}
      {open && (
        <div
          // Backdrop click closes the modal
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            // Stop click on the modal body from bubbling up to the backdrop
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0a1628',
              border: '1px solid #162840',
              borderRadius: 12,
              width: 'min(560px, 90vw)',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #162840',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#e8f4ff' }}>
                  What's New
                </div>
                <div style={{ fontSize: 11, color: '#4a7a9b', marginTop: 2 }}>
                  Recent updates to ShopBuilder Pro
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#6a9fc0',
                  fontSize: 22,
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: 1,
                }}
                title="Close (Esc)"
              >
                ✕
              </button>
            </div>

            {/* Scrollable list of versions */}
            <div style={{ overflow: 'auto', padding: '12px 24px 20px' }}>
              {CHANGELOG.map((release, idx) => (
                <div
                  key={release.version}
                  style={{
                    paddingTop: idx === 0 ? 12 : 18,
                    paddingBottom: 4,
                    borderTop: idx === 0 ? 'none' : '1px solid #162840',
                  }}
                >
                  {/* Version + date header */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#3399ff',
                        letterSpacing: '.02em',
                      }}
                    >
                      v{release.version}
                    </span>
                    {release.title && (
                      <span style={{ fontSize: 13, color: '#e8f4ff', fontWeight: 500 }}>
                        {release.title}
                      </span>
                    )}
                    <span style={{ fontSize: 10, color: '#4a7a9b', marginLeft: 'auto' }}>
                      {release.date}
                    </span>
                  </div>

                  {/* Change list */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
                    {release.changes.map((change, i) => {
                      const tag = TAG_STYLES[change.type] || TAG_STYLES.improvement;
                      return (
                        <li
                          key={i}
                          style={{
                            display: 'flex',
                            gap: 10,
                            alignItems: 'flex-start',
                            padding: '6px 0',
                            fontSize: 12,
                            color: '#c8dff0',
                            lineHeight: 1.5,
                          }}
                        >
                          {/* Type tag pill */}
                          <span
                            style={{
                              flexShrink: 0,
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: '.05em',
                              color: tag.color,
                              background: tag.bg,
                              border: `1px solid ${tag.border}`,
                              borderRadius: 3,
                              padding: '2px 6px',
                              minWidth: 60,
                              textAlign: 'center',
                              marginTop: 1,
                            }}
                          >
                            {tag.label}
                          </span>
                          <span style={{ flex: 1 }}>{change.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '12px 24px',
                borderTop: '1px solid #162840',
                fontSize: 10,
                color: '#4a7a9b',
                textAlign: 'center',
                fontStyle: 'italic',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div>Suggestions or bugs? Reach out to Chris.</div>
              {/* Terms of Service / Privacy review link.
                  Opens TermsModal in review mode (not gate mode) — user already
                  accepted to be using the app, this is just for re-reading. */}
              <button
                onClick={() => setShowTerms(true)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#3399ff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: 10,
                  padding: 0,
                  fontFamily: 'inherit',
                  fontStyle: 'normal',
                }}
              >
                View Terms of Service & Privacy Policy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms review modal — rendered outside the WhatsNew modal so it
          layers above (zIndex 10000 vs 1000). Closing returns to WhatsNew. */}
      {showTerms && (
        <TermsModal forceOpen={true} onClose={() => setShowTerms(false)} />
      )}
    </>
  );
}
